import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Boxes, DollarSign, FolderOpen, MapPin, RefreshCw, TrendingUp } from 'lucide-react';
import Breadcrumb from '../components/ui/Breadcrumb';
import { Card } from '../components/ui/card';
import { buildAnalyticsSnapshot, loadSavedEstimates, STORAGE_KEY } from '../lib/estimateAnalytics';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDate = (value) => {
  if (!value) return 'No saved estimates yet';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleString();
};

const MetricCard = ({ title, value, detail, icon }) => (
  <Card className="p-5 border border-gray-100 dark:border-gray-700">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">{value}</p>
        {detail ? <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{detail}</p> : null}
      </div>
      <div className="p-3 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">{icon}</div>
    </div>
  </Card>
);

const TrendChart = ({ points }) => {
  const maxValue = Math.max(...points.map((point) => point.total), 0);

  return (
    <Card className="p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Spend trend</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Monthly total across saved estimates from the last six save windows.</p>
        </div>
        <TrendingUp className="text-teal-500 dark:text-teal-400" size={20} />
      </div>

      <div className="grid grid-cols-6 gap-3 h-56 items-end">
        {points.map((point) => {
          const height = maxValue > 0 ? `${Math.max(12, (point.total / maxValue) * 100)}%` : '12%';

          return (
            <div key={point.key} className="h-full flex flex-col justify-end gap-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">{formatCurrency(point.total)}</p>
              <div className="flex-1 flex items-end">
                <div className="w-full rounded-t-lg bg-gradient-to-t from-teal-500 to-cyan-400" style={{ height }} />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{point.label}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{point.count} saved</p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const BreakdownCard = ({ title, description, items, total, emptyMessage, icon }) => (
  <Card className="p-6 border border-gray-100 dark:border-gray-700">
    <div className="flex items-start justify-between gap-4 mb-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      <div className="text-teal-500 dark:text-teal-400">{icon}</div>
    </div>

    {items.length ? (
      <div className="space-y-4">
        {items.slice(0, 5).map((item) => {
          const width = total > 0 ? `${Math.max(8, (item.total / total) * 100)}%` : '8%';

          return (
            <div key={item.label}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">{item.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.count} resource entries</p>
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{formatCurrency(item.total)}</p>
              </div>
              <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div className="h-full rounded-full bg-teal-500 dark:bg-teal-900/30" style={{ width }} />
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <p className="text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</p>
    )}
  </Card>
);

const RecentEstimateCard = ({ estimate }) => (
  <Card className="p-5 border border-gray-100 dark:border-gray-700">
    <div className="flex items-start justify-between gap-4 mb-3">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{estimate.name}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(estimate.savedAt)}</p>
      </div>
      <FolderOpen className="text-teal-500 dark:text-teal-400" size={18} />
    </div>
    <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">{formatCurrency(estimate.total)}</p>
    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{estimate.items.length} resources modeled</p>
  </Card>
);

const Analytics = () => {
  const [estimates, setEstimates] = useState(() => loadSavedEstimates());

  const refreshAnalytics = useCallback(() => {
    setEstimates(loadSavedEstimates());
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (!event.key || event.key === STORAGE_KEY) {
        refreshAnalytics();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshAnalytics]);

  const snapshot = useMemo(() => buildAnalyticsSnapshot(estimates), [estimates]);

  if (!snapshot.estimateCount) {
    return (
      <div className="container mx-auto p-4">
        <Breadcrumb items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Analytics' }]} />

        <div className="max-w-3xl mx-auto text-center py-16 px-6">
          <div className="inline-flex items-center justify-center rounded-full bg-teal-50 text-teal-600 p-4 mb-6">
            <BarChart3 size={36} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">No analytics yet</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            Save a few cost estimates to unlock spend trends, resource mix, and regional insights.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/pricing/estimator" className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600">
              Create estimate
            </Link>
            <Link to="/pricing/saved" className="px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200">
              View saved estimates
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <Breadcrumb items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Analytics' }]} />

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Analytics Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            Portfolio insights built from the saved estimates in your browser.
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Latest saved estimate: {formatDate(snapshot.latestSavedAt)}</p>
        </div>
        <button
          onClick={refreshAnalytics}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 w-fit"
        >
          <RefreshCw size={16} />
          Refresh data
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <MetricCard
          title="Portfolio monthly spend"
          value={formatCurrency(snapshot.totalMonthlySpend)}
          detail={`Across ${snapshot.estimateCount} saved estimates`}
          icon={<DollarSign size={20} />}
        />
        <MetricCard
          title="Average estimate value"
          value={formatCurrency(snapshot.averageEstimateTotal)}
          detail="Mean monthly total per saved estimate"
          icon={<TrendingUp size={20} />}
        />
        <MetricCard
          title="Tracked resources"
          value={snapshot.totalResources}
          detail="Resource line items captured in estimates"
          icon={<Boxes size={20} />}
        />
        <MetricCard
          title="Active regions"
          value={snapshot.regionCount}
          detail="Distinct Azure regions referenced"
          icon={<MapPin size={20} />}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        <div className="xl:col-span-2">
          <TrendChart points={snapshot.spendTrend} />
        </div>
        <Card className="p-6 border border-gray-100 dark:border-gray-700">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Key signals</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Quick takeaways from your saved estimate history.</p>
            </div>
            <BarChart3 className="text-teal-500 dark:text-teal-400" size={20} />
          </div>

          <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-500">
            {snapshot.insights.map((insight) => (
              <li key={insight} className="flex gap-2">
                <span className="text-teal-500 dark:text-teal-400">•</span>
                <span>{insight}</span>
              </li>
            ))}
          </ul>

          {snapshot.topEstimate ? (
            <div className="mt-6 pt-5 border-t">
              <p className="text-sm text-gray-500 dark:text-gray-400">Largest estimate</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">{snapshot.topEstimate.name}</p>
              <p className="text-sm text-teal-600 dark:text-teal-400 mt-1">{formatCurrency(snapshot.topEstimate.total)} / month</p>
            </div>
          ) : null}
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        <BreakdownCard
          title="Resource mix"
          description="Top resource categories by modeled monthly spend."
          items={snapshot.resourceBreakdown}
          total={snapshot.totalMonthlySpend}
          emptyMessage="Add resource line items to see category mix."
          icon={<Boxes size={20} />}
        />
        <BreakdownCard
          title="Regional footprint"
          description="Regions showing the most modeled monthly cost."
          items={snapshot.regionBreakdown}
          total={snapshot.totalMonthlySpend}
          emptyMessage="Add regional estimates to see deployment footprint."
          icon={<MapPin size={20} />}
        />
      </div>

      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Recent estimates</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Most recently saved estimates feeding this dashboard.</p>
        </div>
        <Link to="/pricing/saved" className="inline-flex items-center gap-2 text-sm text-teal-600 dark:text-teal-400 hover:underline">
          Open saved estimates
          <ArrowRight size={16} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {snapshot.recentEstimates.map((estimate) => (
          <RecentEstimateCard key={estimate.id} estimate={estimate} />
        ))}
      </div>
    </div>
  );
};

export default Analytics;
